import { InboxSenderMatchOutcome, InboxThreadSender } from './sender.model';
import { InboxThreadDraft } from './thread-draft.model';

export type InboxEmailCategory =
    | 'violations'
    | 'requests'
    | 'invoices'
    | 'bookings'
    | 'voting'
    | 'surveys'
    | 'broadcasts'
    | 'calendar'
    | 'tasks'
    | 'payables'
    | 'message_board'
    | 'resale_docs'
    | 'uncategorized';

export const INBOX_EMAIL_CATEGORIES: InboxEmailCategory[] = [
    'invoices',
    'violations',
    'requests',
    'bookings',
    'voting',
    'surveys',
    'broadcasts',
    'calendar',
    'tasks',
    'payables',
    'message_board',
    'resale_docs',
    'uncategorized',
];

export type InboxEmailAuthorRole = 'homeowner' | 'staff' | 'unconfirmed' | 'contact' | 'external';

export type InboxPartyKind = 'member' | 'user' | 'contact' | 'external' | 'unconfirmed' | 'staff';

export type InboxContactRelationship = 'resident' | 'renter' | 'family' | 'other';

export interface InboxThreadParty
{
    kind: InboxPartyKind;
    name: string | null;
    email: string | null;
    roleLabel?: string | null;
    role?: string | null;
    relationship?: InboxContactRelationship | null;
    unitLabel?: string | null;
}

export interface InboxRecipient
{
    name: string | null;
    email: string;
}

export interface InboxRecipients
{
    to: InboxRecipient[];
    cc: InboxRecipient[];
    notified: InboxRecipient[];
    phone?: string | null;
}

export type InboxEmailMessageKind = 'email' | 'comment' | 'note';

export interface InboxEmailRecord
{
    type: string;
    id: number;
    label: string;
    url: string | null;
}

export interface InboxEmailHomeowner
{
    membershipId: number | null;
    name: string | null;
    email: string | null;
}

export interface InboxEmailThreadInfo
{
    id: number;
    category: InboxEmailCategory;
    status: 'needs_reply' | 'resolved';
    readStatus: 'read' | 'unread';
    isSpam: boolean;
    address: string | null;
    record: InboxEmailRecord | null;
    homeowner: InboxEmailHomeowner | null;
    sender?: InboxThreadSender | null;
    senderVerified?: boolean | null;
    replyTarget?: InboxEmailReplyTarget | null;
    party?: InboxThreadParty | null;
    lastFromStaff?: boolean;
}

export interface InboxEmailReplyTarget
{
    address: string | null;
    confirmed: boolean;
}

export interface InboxEmailAttachment
{
    id: number;
    name: string;
    size: number | null;
    url: string;
}

export type InboxDeliveryStatus = 'sending' | 'sent' | 'failed';

export interface InboxEmailMessage
{
    id: number;
    direction?: 'in' | 'out' | null;
    kind: InboxEmailMessageKind;
    authorName: string | null;
    authorRole: InboxEmailAuthorRole;
    fromEmail: string | null;
    senderVerified?: boolean | null;
    subject: string | null;
    body: string | null;
    html: string | null;
    createdAt: string;
    attachments: InboxEmailAttachment[];
    adminOnly: boolean;
    inboxEmailId?: number | null;
    recipients?: InboxRecipients | null;
    threadId?: number | null;
    deliveryStatus?: InboxDeliveryStatus | null;
    failureReason?: string | null;
}

export interface InboxEmailThread
{
    thread: InboxEmailThreadInfo;
    messages: InboxEmailMessage[];
    draft?: InboxThreadDraft | null;
}

export interface InboxEmailMatchResult extends InboxEmailThread, InboxSenderMatchOutcome
{
}

export interface InboxEmailReplyPayload
{
    message: string;
    internal?: boolean;
    markResolved?: boolean;
}

export interface InboxEmailFileTarget
{
    type: 'violation' | 'request';
    id: number;
}

export interface InboxEmailToRequestPayload
{
    formId: number;
    unitId?: number | null;
}

export function inboxEmailIsRecordCategory(category: string | null | undefined): boolean {
    return category === 'violations' || category === 'requests';
}

export function inboxEmailRecordLink(record: InboxEmailRecord | null): string | null {
    if (!record) {
        return null;
    }
    if (record.url) {
        return /^https?:\/\//i.test(record.url) ? inboxUrlPath(record.url) : record.url;
    }
    const type = (record.type ?? '').toLowerCase();
    if (type.includes('violation')) {
        return `/app/violations/detail/${record.id}`;
    }
    if (type.includes('request') || type.includes('submission')) {
        return `/app/requests/submission/${record.id}`;
    }
    return null;
}

export function inboxUrlPath(url: string): string {
    try {
        const parsed = new URL(url);
        return parsed.pathname + parsed.search + parsed.hash;
    } catch {
        return url;
    }
}

const CONTACT_RELATIONSHIP_LABELS: Record<InboxContactRelationship, string> = {
    resident: 'Resident',
    renter: 'Renter',
    family: 'Family member',
    other: 'Contact',
};

export const INBOX_EXTERNAL_ROLE_LABELS: Record<string, string> = {
    vendor: 'Vendor',
    title_company: 'Title company',
    agent: 'Agent',
    buyer: 'Buyer',
    seller: 'Seller',
    requester: 'Requester',
};

export function inboxPartyIsOffFile(kind: string | null | undefined): boolean {
    return kind === 'contact' || kind === 'external';
}

export function inboxPartyLabel(party: InboxThreadParty | null | undefined): string | null {
    if (!party) {
        return null;
    }
    if (party.kind === 'contact') {
        if (!party.relationship && !party.unitLabel && party.roleLabel) {
            return party.roleLabel;
        }
        const who = CONTACT_RELATIONSHIP_LABELS[party.relationship ?? 'other'] ?? 'Contact';
        return party.unitLabel ? `${who} at ${party.unitLabel}, not an owner` : `${who}, not an owner`;
    }
    if (party.kind === 'external') {
        return party.roleLabel || INBOX_EXTERNAL_ROLE_LABELS[party.role ?? ''] || 'External';
    }
    return party.roleLabel ?? null;
}
